/* eslint-disable */
import React, { useState, useMemo } from 'react';
import StatusPill from './StatusPill';
import ScanOutSheet from './ScanOutSheet';
import ScanInSheet from './ScanInSheet';
import SwapSheet from './SwapSheet';
import CloseShiftSheet from './CloseShiftSheet';

/**
 * ShiftDetailDesktop — full desktop view for a single shift with allocations table.
 * Includes modal overlays for scan-out, scan-in, swap, and close-shift flows.
 */
const GRID_COLS = '1.6fr 1fr 1.4fr 1.2fr 0.6fr 0.6fr 0.8fr 0.8fr 0.85fr';

export default function ShiftDetailDesktop({ st, setSt, shiftId, account, audit, isAdmin, isManager, onBack, onSelectShift }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null); // null | 'scan-out' | 'scan-in' | 'swap' | 'close-shift'
  const [scanInPrefillAllocId, setScanInPrefillAllocId] = useState(null);
  const [swapPrefillAllocId, setSwapPrefillAllocId] = useState(null);

  const shift = (st.shifts || []).find(s => s.id === shiftId);
  const allocs = useMemo(() => (st.allocations || []).filter(a => a.shiftId === shiftId), [st.allocations, shiftId]);
  const loc = shift ? (st.locations || []).find(l => l.id === shift.locationId) : null;

  if (!shift) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Shift not found. <button onClick={onBack} className="btn btn-ghost">Back to shifts</button></div>;

  const effectiveStatus = shift.status === 'planned' && new Date(shift.startAt) <= new Date() ? 'active' : shift.status;
  const isActive = effectiveStatus === 'active';
  const isClosed = effectiveStatus === 'closed' || effectiveStatus === 'closed_with_open_items';

  const outCount = allocs.filter(a => a.status === 'out').length;
  const returnedCount = allocs.filter(a => a.status === 'returned').length;
  const flaggedCount = allocs.filter(a => a.status === 'flagged_missing').length;
  const swapCount = allocs.filter(a => a.swappedFromAllocationId || a.swappedToAllocationId).length;
  const totalCount = allocs.length;

  const longestOut = useMemo(() => {
    const outs = allocs.filter(a => a.status === 'out');
    if (!outs.length) return '—';
    const ms = Math.max(...outs.map(a => Date.now() - new Date(a.outAt).getTime()));
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  }, [allocs]);

  let filtered = filter === 'all' ? allocs : allocs.filter(a => a.status === filter);
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(a => {
      const dev = (st.devices || []).find(d => d.id === a.deviceId);
      return (a.anonDeviceLabel || dev?.serial || '').toLowerCase().includes(q) || (a.role || '').toLowerCase().includes(q) || (a.operativeName || '').toLowerCase().includes(q);
    });
  }
  const sorted = [...filtered].sort((a, b) => new Date(b.outAt) - new Date(a.outAt));

  // Eligible devices for this shift — assigned to the shift's location, available, and not already allocated on this shift
  const eligibleDevices = useMemo(() => {
    const allocatedDeviceIds = new Set(allocs.filter(a => a.status === 'out').map(a => a.deviceId));
    return (st.devices || []).filter(d => {
      if (allocatedDeviceIds.has(d.id)) return false;
      if (d.availability && d.availability !== 'available') return false;
      const matchesById = d.assignedLocationId && d.assignedLocationId === shift.locationId;
      const matchesByName = d.location && loc?.name && d.location === loc.name;
      const unassigned = !d.assignedLocationId && !d.location;
      return matchesById || matchesByName || unassigned;
    }).sort((a, b) => (a.serial || '').localeCompare(b.serial || ''));
  }, [st.devices, allocs, shift.locationId, loc?.name]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
  const fmtDateTimeShort = (d) => d ? `${fmtDateShort(d)}, ${fmtTime(d)}` : '—';

  const nextShift = useMemo(() => {
    if (!shift) return null;
    return (st.shifts || [])
      .filter(s => s.locationId === shift.locationId && s.id !== shift.id && new Date(s.startAt) > new Date(shift.startAt))
      .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))[0] || null;
  }, [st.shifts, shift]);
  const duration = (outAt, inAt) => {
    if (!outAt) return '—';
    const end = inAt ? new Date(inAt) : new Date();
    const ms = end - new Date(outAt);
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  const handleStartShift = () => {
    setSt(prev => ({ ...prev, shifts: prev.shifts.map(s => s.id === shiftId ? { ...s, status: 'active', startedAt: new Date().toISOString() } : s) }));
    audit('shift_started', { shiftId, title: shift.title });
  };

  const handleReturnAlloc = (allocId) => {
    const alloc = allocs.find(a => a.id === allocId);
    if (!alloc || alloc.status !== 'out') return;
    const now = new Date().toISOString();
    setSt(prev => ({
      ...prev,
      allocations: prev.allocations.map(a => a.id === allocId ? { ...a, status: 'returned', inAt: now } : a),
      devices: prev.devices.map(d => d.id === alloc.deviceId ? { ...d, availability: 'available' } : d),
    }));
    audit('allocation_returned', { shiftId, allocationId: allocId });
  };

  // ── Modal overlay for mobile scan flows ──
  if (modal) {
    const modalContent = {
      'scan-out': (
        <ScanOutSheet st={st} setSt={setSt} shiftId={shiftId} account={account} audit={audit}
          onBack={() => setModal(null)}
          onSuccess={() => setModal(null)} />
      ),
      'scan-in': (
        <ScanInSheet st={st} setSt={setSt} shiftId={shiftId} account={account} audit={audit}
          prefilledAllocationId={scanInPrefillAllocId}
          onBack={() => { setModal(null); setScanInPrefillAllocId(null); }}
          onSuccess={() => { setModal(null); setScanInPrefillAllocId(null); }} />
      ),
      'swap': (
        <SwapSheet st={st} setSt={setSt} shiftId={shiftId} account={account} audit={audit}
          prefilledAllocationId={swapPrefillAllocId}
          onBack={() => { setModal(null); setSwapPrefillAllocId(null); }}
          onSuccess={() => { setModal(null); setSwapPrefillAllocId(null); }} />
      ),
      'close-shift': (
        <CloseShiftSheet st={st} setSt={setSt} shiftId={shiftId} account={account} audit={audit}
          onBack={() => setModal(null)}
          onSuccess={() => { setModal(null); onBack(); }} />
      ),
    }[modal];

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', padding: 20 }}>
        <div style={{
          background: 'var(--surface-app)', border: '1px solid var(--border-default)',
          borderRadius: 20, width: '100%', maxWidth: 520, maxHeight: '90vh',
          overflow: 'auto', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
        }}>
          {modalContent}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, fontSize: 13 }}>Shifts</button>
        <span style={{ color: 'var(--text-subtle)' }}>/</span>
        <span style={{ color: 'var(--text-muted)' }}>{loc?.name || ''}</span>
        <span style={{ color: 'var(--text-subtle)' }}>/</span>
        <span style={{ color: 'var(--text-default)', fontWeight: 500 }}>Shift {shift.shiftNumber} · {shift.possessionRef}</span>
      </div>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-default)', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            Shift {shift.shiftNumber} · {shift.title}
            <StatusPill status={effectiveStatus} />
            {shift.anonymousMode && <span style={{ padding: '3px 8px', background: 'rgba(191,128,255,0.15)', color: '#bf80ff', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}>ANON</span>}
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {shift.possessionRef} · {fmtDate(shift.startAt)} · {fmtTime(shift.startAt)} → {fmtTime(shift.endAt)} · Week {shift.week}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {effectiveStatus === 'planned' && <button onClick={handleStartShift} className="btn btn-primary">Start shift</button>}
          {isActive && (
            <>
              <button onClick={() => setModal('scan-out')} className="btn btn-primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>
                Scan Out
              </button>
              <button onClick={() => setModal('scan-in')} className="btn btn-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                Scan In
              </button>
              <button onClick={() => setModal('swap')} className="btn btn-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4M3 8l4-4 4 4"/><path d="M17 8v12m4-4l-4 4-4-4"/></svg>
                Swap
              </button>
              <button onClick={() => setModal('close-shift')} className="btn btn-ghost">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                Close shift
              </button>
            </>
          )}
          {isClosed && (
            <StatusPill status={effectiveStatus} />
          )}
        </div>
      </div>

      {/* Closed banner */}
      {isClosed && (
        <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          This shift is closed. Allocations are read-only.
        </div>
      )}

      {/* Shift metadata bar */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 18, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20 }}>
        <div><div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>Location</div><div style={{ fontSize: 13, color: 'var(--text-default)', fontWeight: 500 }}>{loc?.name || '—'}</div></div>
        <div><div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>Site</div><div style={{ fontSize: 13, color: 'var(--text-default)', fontWeight: 500 }}>{shift.site || loc?.name || '—'}</div></div>
        <div><div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>Supervisor</div><div style={{ fontSize: 13, color: 'var(--text-default)', fontWeight: 500 }}>{shift.supervisorUserId || '—'}</div></div>
        <div><div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>Window</div><div style={{ fontSize: 13, color: 'var(--text-default)', fontWeight: 500 }}>{fmtTime(shift.startAt)} → {fmtTime(shift.endAt)}</div></div>
        <div><div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>Shift / Week</div><div style={{ fontSize: 13, color: 'var(--text-default)', fontWeight: 500 }}>{shift.shiftNumber} / {shift.week}</div></div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Total</div><div style={{ fontSize: 24, fontWeight: 700, color: 'var(--brand-default)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{totalCount}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Out</div><div style={{ fontSize: 24, fontWeight: 700, color: '#fbbf24', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{outCount}</div><div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>Longest: {longestOut}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Returned</div><div style={{ fontSize: 24, fontWeight: 700, color: '#4ade80', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{returnedCount}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Swaps</div><div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{Math.floor(swapCount / 2)}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Flagged</div><div style={{ fontSize: 24, fontWeight: 700, color: flaggedCount > 0 ? '#f87171' : 'var(--text-default)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{flaggedCount}</div></div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="input-with-icon" style={{ width: 240 }}>
          <span className="input-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search devices..." style={{ width: '100%' }} />
          {search && <button onClick={() => setSearch('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>}
        </div>
        {[
          { key: 'all', label: 'All', count: totalCount },
          { key: 'out', label: 'Out', count: outCount },
          { key: 'returned', label: 'Returned', count: returnedCount },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="btn btn-ghost"
            style={{
              background: filter === f.key ? 'var(--brand-tint)' : undefined,
              borderColor: filter === f.key ? 'var(--brand-border)' : undefined,
              color: filter === f.key ? 'var(--brand-default)' : undefined,
            }}
          >
            {f.label} · {f.count}
          </button>
        ))}
      </div>

      {/* Two-column layout: allocations table + eligible devices sidebar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 18, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
      {/* Allocations grid table */}
      {sorted.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>No allocations{filter !== 'all' ? ' matching filter' : ''}</div>
          {filter === 'all' && isActive && (
            <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6 }}>
              Use <strong>Scan Out</strong> above to allocate a device to this shift.
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6, paddingLeft: 2 }}>
            {sorted.length} allocation{sorted.length !== 1 ? 's' : ''}{filter !== 'all' ? ` (${filter})` : ''}
          </div>
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, gap: 12, padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
              {['Device', 'Role', 'Operative', 'Phone', 'Out', 'In', 'Duration', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: 0.6, textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {sorted.map(alloc => {
              const device = (st.devices || []).find(d => d.id === alloc.deviceId);
              const isReturned = alloc.status === 'returned' || alloc.status === 'carried_over';
              const isFlagged = alloc.status === 'flagged_missing';
              const dur = duration(alloc.outAt, alloc.inAt);
              const durMs = (alloc.inAt ? new Date(alloc.inAt) : new Date()) - new Date(alloc.outAt);
              const durStyle = durMs > 10 * 3600000 ? { color: 'var(--danger-text)', fontWeight: 600 } : durMs > 6 * 3600000 ? { color: 'var(--warning-text)', fontWeight: 600 } : {};

              return (
                <div
                  key={alloc.id}
                  style={{
                    display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center', gap: 12,
                    padding: '12px 16px', background: 'var(--surface-default)', borderBottom: '1px solid var(--border-default)',
                    opacity: isReturned ? 0.55 : 1, transition: 'background 0.12s', cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-inset)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-default)'; }}
                >
                  {/* Device */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: isFlagged ? 'var(--danger-text)' : isReturned ? 'var(--success-text)' : 'var(--warning-text)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: alloc.anonDeviceLabel ? 'monospace' : 'inherit' }}>{alloc.anonDeviceLabel || device?.serial || alloc.deviceId?.toString() || '?'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{device?.type || ''}</div>
                    </div>
                  </div>
                  {/* Role */}
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, minWidth: 0 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {alloc.role}
                      {(alloc.swappedFromAllocationId || alloc.swappedToAllocationId) && (
                        <span style={{ marginLeft: 6, padding: '1px 5px', background: 'var(--info-fill)', color: 'var(--info-text)', border: '1px solid var(--info-border)', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 0.4 }}>SWAP</span>
                      )}
                    </div>
                  </div>
                  {/* Operative */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: alloc.operativeName ? 'var(--text-primary)' : 'var(--text-ghost)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: alloc.operativeName ? 'normal' : 'italic' }}>
                      {alloc.operativeName || 'Unassigned plant'}
                    </div>
                    {alloc.swappedFromAllocationId && <div style={{ fontSize: 10, color: 'var(--info-text)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>← From swap</div>}
                    {alloc.swappedToAllocationId && <div style={{ fontSize: 10, color: 'var(--info-text)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>→ Swapped</div>}
                  </div>
                  {/* Phone */}
                  <div style={{ fontSize: 12, fontFamily: 'monospace', color: alloc.operativePhone ? 'var(--text-primary)' : 'var(--text-ghost)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alloc.operativePhone || '—'}</div>
                  {/* Out */}
                  <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtTime(alloc.outAt)}</div>
                  {/* In */}
                  <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: alloc.inAt ? 'var(--text-primary)' : 'var(--text-ghost)' }}>{alloc.inAt ? fmtTime(alloc.inAt) : '—'}</div>
                  {/* Duration */}
                  <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', ...durStyle }}>{dur}</div>
                  {/* Status */}
                  <div><StatusPill status={alloc.status} /></div>
                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                    {alloc.status === 'out' && !isClosed && (
                      <>
                        <button
                          onClick={() => { setScanInPrefillAllocId(alloc.id); setModal('scan-in'); }}
                          title="Return device"
                          aria-label="Return device"
                          style={{ width: 28, height: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--success-fill, rgba(74,222,128,0.15))'; e.currentTarget.style.color = 'var(--success-text, #4ade80)'; e.currentTarget.style.borderColor = 'var(--success-text, #4ade80)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        </button>
                        <button
                          onClick={() => { setSwapPrefillAllocId(alloc.id); setModal('swap'); }}
                          title="Swap device"
                          aria-label="Swap device"
                          style={{ width: 28, height: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--info-fill, rgba(96,165,250,0.15))'; e.currentTarget.style.color = 'var(--info-text, #60a5fa)'; e.currentTarget.style.borderColor = 'var(--info-text, #60a5fa)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4M3 8l4-4 4 4"/><path d="M17 8v12m4-4l-4 4-4-4"/></svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
        </div>

        {/* Sidebar: shift info, next shift, eligible devices */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Shift Info card */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Shift Info</div>
            {[
              { label: 'Possession ref', value: shift.possessionRef || '—' },
              { label: 'Client', value: loc?.customer || '—' },
              { label: 'Created by', value: shift.createdByUserId || '—' },
              { label: 'Created', value: fmtDateTimeShort(shift.createdAt) },
              { label: 'Started', value: shift.startedAt ? fmtDateTimeShort(shift.startedAt) : '—' },
            ].map((row, i, arr) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '8px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-default)', fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.value}</div>
              </div>
            ))}
          </div>

          {/* Next Shift card */}
          {nextShift && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Next Shift</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-default)', marginBottom: 4 }}>
                Shift {nextShift.shiftNumber} · {fmtDateShort(nextShift.startAt)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                {fmtTime(nextShift.startAt)} → {fmtTime(nextShift.endAt)}{nextShift.supervisorUserId ? ` · ${nextShift.supervisorUserId}` : ''}
              </div>
              <div style={{ marginBottom: 12 }}>
                <StatusPill status={nextShift.status === 'planned' && new Date(nextShift.startAt) <= new Date() ? 'active' : nextShift.status} />
              </div>
              {onSelectShift && (
                <button
                  type="button"
                  onClick={() => onSelectShift(nextShift.id)}
                  className="btn btn-ghost"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  View next shift
                </button>
              )}
            </div>
          )}

          {/* Eligible devices */}
          <div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6, paddingLeft: 2 }}>
            {eligibleDevices.length} available device{eligibleDevices.length !== 1 ? 's' : ''} at {loc?.name || 'this location'}
          </div>
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface-default)' }}>
            <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: 0.6, textTransform: 'uppercase' }}>Device</div>
            </div>
            {eligibleDevices.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--text-subtle)', textAlign: 'center' }}>
                No available devices at this location.
              </div>
            ) : (
              eligibleDevices.map(d => (
                <div
                  key={d.id}
                  style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-default)' }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.serial}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{d.type}</div>
                  </div>
                </div>
              ))
            )}
          </div>
          </div>
        </div>
      </div>

    </div>
  );
}
